(() => {
  const DISMISS_KEY = 'fullykitted:reviews:dismissed';
  const ROOT_SELECTOR = '[data-rv-root]';
  const FLOATERS_SELECTOR = '[data-rv-floaters]';
  const PLAYER_SELECTOR = '[data-rv-player]';
  const OPEN_BUTTON_SELECTOR = '[data-rv-open]';

  // Fixed-rail geometry mirrors review-videos.css (env safe-area inset is
  // covered by the 8px overlap margin instead of being predicted exactly).
  const RAIL_BREAKPOINT = 800;
  const RAIL_OFFSETS = {
    wide: { left: 12, bottom: 16 },
    narrow: { left: 8, bottom: 100 },
  };
  // Last-resort estimate until the rail has been measured visible once.
  // Any stable value works: it only needs to not depend on hidden state,
  // so show/hide cannot oscillate around the same CTA overlap.
  const RAIL_DEFAULT_SIZE = { width: 300, height: 68 };

  // In-page dismissal memory. Covers browsers where sessionStorage access
  // or writes throw (denied storage): the rail still stays dismissed for
  // the current page even though nothing persists across navigations.
  let memoryDismissed = false;

  const isDesignMode = () => Boolean(window.Shopify?.designMode);

  // window.sessionStorage itself can throw on access (blocked cookies), so
  // resolve it behind a guard instead of reading the property directly.
  function safeStorage(win) {
    try {
      return win.sessionStorage;
    } catch {
      return null;
    }
  }

  function readDismissed(storage) {
    if (isDesignMode()) return false;
    if (memoryDismissed) return true;
    try {
      return !!storage && storage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  }

  function writeDismissed(storage) {
    if (isDesignMode()) return;
    memoryDismissed = true;
    try {
      if (storage) storage.setItem(DISMISS_KEY, '1');
    } catch {
      // Storage denied: memoryDismissed above keeps this page dismissed.
    }
  }

  // Test hook: clears the in-page dismissal memory between checks.
  function resetMemoryDismissal() {
    memoryDismissed = false;
  }

  function overlayOpen(doc) {
    if (doc.querySelector('[data-cart-drawer][open]')) return true;
    return Boolean(doc.querySelector('dialog[open]'));
  }

  function menuOpen(doc) {
    return Boolean(doc.querySelector('[data-mobile-menu].is-open'));
  }

  function isVisible(el) {
    if (!el || el.hidden) return false;
    if (typeof el.getBoundingClientRect === 'function') {
      let rect = null;
      try {
        rect = el.getBoundingClientRect();
      } catch {
        return false;
      }
      if (!rect || rect.width === 0 || rect.height === 0) return false;
    }
    if (typeof window.getComputedStyle === 'function') {
      try {
        if (window.getComputedStyle(el).display === 'none') return false;
      } catch {
        // Ignore: rect check above is the decisive signal.
      }
    }
    return true;
  }

  function rectsOverlap(a, b, margin) {
    return a.left < b.right + margin
      && a.right > b.left - margin
      && a.top < b.bottom + margin
      && a.bottom > b.top - margin;
  }

  // The quiet bulk-offer teaser ([data-promo-teaser]) is NOT a blanket
  // visibility gate: it coexists with the reviews when spatially separate
  // (desktop lower-left rail vs centred teaser; mobile rail above teaser)
  // and hides them only when their actual regions overlap.
  function purchaseCtaOverlap(doc, floatersRect) {
    if (!floatersRect) return false;
    const candidates = doc.querySelectorAll(
      '.hero-actions .button, .product-tease-copy .button, .home-cta .button, [data-purchase-bar]:not([hidden])'
    );
    for (const node of candidates) {
      if (typeof node.getBoundingClientRect !== 'function') continue;
      const rect = node.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) continue;
      if (rectsOverlap(rect, floatersRect, 8)) return true;
    }
    if (typeof doc.querySelector === 'function') {
      let teaser = null;
      try {
        teaser = doc.querySelector('[data-promo-teaser]');
      } catch {
        teaser = null;
      }
      if (teaser && !teaser.hidden && typeof teaser.getBoundingClientRect === 'function') {
        let rect = null;
        try {
          rect = teaser.getBoundingClientRect();
        } catch {
          rect = null;
        }
        if (rect && !(rect.width === 0 && rect.height === 0)) {
          if (rectsOverlap(rect, floatersRect, 8)) return true;
        }
      }
    }
    return false;
  }

  // Stable rail region for the current viewport, used when the rail is
  // hidden (its live rect is 0x0 then). Never returns a zero-area rect, so
  // collision state cannot toggle around the same overlap.
  function predictedRailRect(size, viewport) {
    const narrow = (viewport.width || 0) <= RAIL_BREAKPOINT;
    const off = narrow ? RAIL_OFFSETS.narrow : RAIL_OFFSETS.wide;
    const width = Math.max(0, size.width || 0);
    const height = Math.max(0, size.height || 0);
    const bottom = Math.max(0, (viewport.height || 0) - off.bottom);
    return {
      left: off.left,
      top: bottom - height,
      right: off.left + width,
      bottom,
      width,
      height,
    };
  }

  // Live rect when the rail is laid out; otherwise the last measured size
  // re-anchored to the current viewport (or a fixed estimate before the
  // first visible measurement). The result is stable while hidden.
  function measureRailRect(floaters, cache) {
    let live = null;
    if (floaters && typeof floaters.getBoundingClientRect === 'function') {
      try {
        live = floaters.getBoundingClientRect();
      } catch {
        live = null;
      }
    }
    if (live && live.width > 0 && live.height > 0) {
      cache.width = live.width;
      cache.height = live.height;
      return live;
    }
    const size = (cache.width > 0 && cache.height > 0)
      ? { width: cache.width, height: cache.height }
      : RAIL_DEFAULT_SIZE;
    return predictedRailRect(size, {
      width: window.innerWidth || 0,
      height: window.innerHeight || 0,
    });
  }

  function shouldShowFloaters(state) {
    if (!state.enabled) return false;
    if (!state.hasVideo) return false;
    if (state.dismissed) return false;
    if (state.heroInView) return false;
    if (state.overlay) return false;
    if (state.menu) return false;
    if (state.ctaOverlap) return false;
    return true;
  }

  function collectState(doc, storage, heroInView, floatersRect) {
    return {
      enabled: true,
      hasVideo: true,
      dismissed: readDismissed(storage),
      heroInView,
      overlay: overlayOpen(doc),
      menu: menuOpen(doc),
      ctaOverlap: purchaseCtaOverlap(doc, floatersRect),
    };
  }

  function attachSources(video) {
    let attached = false;
    for (const source of video.querySelectorAll('source[data-src]')) {
      if (!source.getAttribute('src')) {
        source.setAttribute('src', source.getAttribute('data-src'));
        attached = true;
      }
    }
    return attached;
  }

  function clearSources(video) {
    try { video.pause(); } catch { /* noop */ }
    for (const source of video.querySelectorAll('source')) source.removeAttribute('src');
    try { video.load(); } catch { /* noop */ }
    video.removeAttribute('src');
  }

  function closePlayer(doc, dialog) {
    if (!(dialog instanceof HTMLDialogElement)) return;
    const video = dialog.querySelector('video');
    if (video) clearSources(video);
    if (dialog.open) dialog.close();
  }

  function closeAllPlayers(doc, except) {
    for (const dialog of doc.querySelectorAll(PLAYER_SELECTOR)) {
      if (dialog !== except) closePlayer(doc, dialog);
    }
  }

  function openPlayer(doc, dialog, trigger) {
    if (!(dialog instanceof HTMLDialogElement)) return false;
    const video = dialog.querySelector('video');
    if (!video) return false;
    closeAllPlayers(doc, dialog);
    if (attachSources(video)) {
      try { video.load(); } catch { /* noop */ }
    }
    try {
      if (!dialog.open) dialog.showModal();
    } catch {
      const first = video.querySelector('source[data-src]');
      const url = first && first.getAttribute('data-src');
      if (url) window.open(url, '_blank', 'noopener');
      return false;
    }
    dialog._rvTrigger = trigger || null;
    const closeButton = dialog.querySelector('[data-rv-close]');
    if (closeButton && typeof closeButton.focus === 'function') {
      closeButton.focus({ preventScroll: true });
    }
    const playPromise = video.play ? video.play() : null;
    if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
    return true;
  }

  // Focus return after close: the original trigger only when it is still
  // visible, else another visible review opener in the same root, else
  // MainContent. Never steals focus when an independently open external
  // overlay (cart, offer, menu dialog) owns it.
  function returnFocus(doc, dialog) {
    const trigger = dialog._rvTrigger;
    dialog._rvTrigger = null;
    if (typeof doc.querySelector === 'function' && overlayOpen(doc)) return;
    if (trigger && trigger.isConnected && typeof trigger.focus === 'function' && isVisible(trigger)) {
      trigger.focus({ preventScroll: true });
      return;
    }
    const root = (typeof dialog.closest === 'function') ? dialog.closest(ROOT_SELECTOR) : null;
    if (root && typeof root.querySelectorAll === 'function') {
      const openers = root.querySelectorAll(OPEN_BUTTON_SELECTOR);
      for (const opener of openers) {
        if (opener !== trigger && isVisible(opener) && typeof opener.focus === 'function') {
          opener.focus({ preventScroll: true });
          return;
        }
      }
    }
    const main = (typeof doc.querySelector === 'function') ? doc.querySelector('#MainContent') : null;
    if (main) {
      try {
        if (typeof main.hasAttribute === 'function'
          && typeof main.setAttribute === 'function'
          && !main.hasAttribute('tabindex')) {
          main.setAttribute('tabindex', '-1');
        }
      } catch {
        // Ignore: focusing still works when the node accepts it.
      }
      if (typeof main.focus === 'function') main.focus({ preventScroll: true });
    }
  }

  function initializeRoot(doc, storage, root) {
    if (root.dataset.rvReady === '1') return;
    root.dataset.rvReady = '1';
    const floaters = root.querySelector(FLOATERS_SELECTOR);
    const hero = doc.querySelector('[data-pinned-hero]');
    let heroInView = true;
    let scheduled = false;
    const railCache = { width: 0, height: 0 };

    const sync = () => {
      scheduled = false;
      if (!floaters) return;
      const rect = measureRailRect(floaters, railCache);
      const state = collectState(doc, storage, heroInView, rect);
      state.enabled = root.dataset.rvEnabled !== '0';
      state.hasVideo = root.dataset.rvHasVideo === '1';
      const show = isDesignMode() ? state.enabled && state.hasVideo : shouldShowFloaters(state);
      if (floaters.hidden === show) floaters.hidden = !show;
    };

    const schedule = () => {
      if (scheduled) return;
      scheduled = true;
      const raf = window.requestAnimationFrame || ((fn) => window.setTimeout(fn, 0));
      raf(sync);
    };

    if (floaters) {
      if (hero && 'IntersectionObserver' in window) {
        const heroObserver = new IntersectionObserver(
          (entries) => {
            heroInView = entries.some((entry) => entry.isIntersecting);
            sync();
          },
          { threshold: 0 }
        );
        heroObserver.observe(hero);
        root._rvObservers = (root._rvObservers || []).concat(heroObserver);
      } else {
        heroInView = (window.scrollY || 0) < (window.innerHeight || 800);
      }

      const dismiss = root.querySelector('[data-rv-dismiss]');
      dismiss?.addEventListener('click', () => {
        writeDismissed(storage);
        sync();
      });

      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule);

      const guard = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          const target = mutation.target;
          if (target instanceof Element && target.closest(ROOT_SELECTOR)) continue;
          schedule();
          return;
        }
      });
      guard.observe(doc.body || doc.documentElement, {
        attributes: true,
        attributeFilter: ['open', 'hidden', 'class'],
        subtree: true,
      });
      root._rvObservers = (root._rvObservers || []).concat(guard);
    }

    for (const dialog of root.querySelectorAll(PLAYER_SELECTOR)) {
      const video = dialog.querySelector('video');
      if (!video) continue;
      dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        closePlayer(doc, dialog);
      });
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) closePlayer(doc, dialog);
      });
      dialog.addEventListener('close', () => {
        if (video.querySelector('source[src]')) clearSources(video);
        // Resync synchronously: a rail hidden while the player was open
        // re-evaluates now instead of staying hidden until the next scroll,
        // and focus below lands on a trigger whose visibility is current.
        sync();
        returnFocus(doc, dialog);
      });
      dialog.querySelectorAll('[data-rv-close]').forEach((button) => {
        button.addEventListener('click', () => closePlayer(doc, dialog));
      });
    }

    root.querySelectorAll('[data-rv-open]').forEach((button) => {
      button.addEventListener('click', () => {
        const key = button.getAttribute('data-rv-open');
        const dialog = key
          ? root.querySelector(`${PLAYER_SELECTOR}[data-rv-key="${key}"]`)
            || doc.querySelector(`${PLAYER_SELECTOR}[data-rv-key="${key}"]`)
          : null;
        if (dialog) {
          openPlayer(doc, dialog, button);
          if (floaters) sync();
        }
      });
    });

    sync();
  }

  function initializeAll(doc, storage) {
    doc.querySelectorAll(ROOT_SELECTOR).forEach((root) => initializeRoot(doc, storage, root));
  }

  const api = {
    DISMISS_KEY,
    readDismissed,
    writeDismissed,
    resetMemoryDismissal,
    safeStorage,
    overlayOpen,
    menuOpen,
    isVisible,
    purchaseCtaOverlap,
    predictedRailRect,
    measureRailRect,
    shouldShowFloaters,
    collectState,
    attachSources,
    clearSources,
    openPlayer,
    closePlayer,
    closeAllPlayers,
    returnFocus,
    initializeRoot,
    initializeAll,
  };
  window.FullyKittedReviews = api;

  if (typeof document !== 'undefined') {
    const boot = () => initializeAll(document, safeStorage(window));
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot, { once: true });
    } else {
      boot();
    }
    document.addEventListener('shopify:section:load', (event) => {
      const scope = event.target instanceof Element ? event.target : document;
      scope.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => initializeRoot(document, safeStorage(window), root));
    });
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})();
